// src/hooks/useReduxReminder.ts
// Consolidated reminder hook - combines Redux state management with service operations

import { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  setReminders,
  addReminder,
  updateReminder,
  removeReminder,
  setReminderPopup,
  findNoteReminder,
  type Reminder,
  type ReminderNote,
  type ReminderPopup,
} from "../store/slices/reminderSlice";
import { reminderService } from "../services/index";
import { useWorkspace } from "@workspace/hooks/useReduxWorkspace";
import { useBanner } from "./useBanner";
import { useUser } from "./useAuth";
import { errorMessage } from "@jkast90/kast-common/format";
import { withErrorHandling } from "../utils/errorUtils";
import type {
  CreateReminderData,
  UpdateReminderData,
} from "../services/reminderService";
import { logger } from "../utils/logger";

const LS_KEY = "note_reminders";
const MINUTE = 60_000;

export function useReduxReminder() {
  const dispatch = useAppDispatch();
  const reminderState = useAppSelector((state) => state.reminder);
  // One Audio for the hook's life, fetched when a reminder first plays it:
  // `useRef(new Audio(...))` built (and loaded) a new one on every render, a
  // request each time, and each failed offline.
  const [ding] = useState(() => {
    if (typeof Audio === "undefined") return null;
    const audio = new Audio("/ding.mp3");
    audio.preload = "none";
    return audio;
  });
  const lastNotifiedId = useRef<number | null>(null);

  // Service layer dependencies
  const { loadAllWorkspaces } = useWorkspace();
  const { showBanner } = useBanner();
  const { userEmail } = useUser();

  const refreshAfterMutation = useCallback(
    () => loadAllWorkspaces({ forceRefresh: true }),
    [loadAllWorkspaces],
  );

  // Load from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(LS_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          dispatch(setReminders(parsed));
        }
      } catch (err) {
        logger.error("Failed to parse reminders from localStorage", err);
      }
    }
  }, [dispatch]);

  // Save to localStorage whenever reminders change
  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify(reminderState.reminders));
  }, [reminderState.reminders]);

  // Ask for notification permissions
  useEffect(() => {
    if ("Notification" in window) {
      logger.debug(
        "[ReduxReminder] Current notification permission:",
        Notification.permission,
      );
      if (Notification.permission === "default") {
        Notification.requestPermission().then((permission) => {
          logger.debug(
            "[ReduxReminder] Notification permission result:",
            permission,
          );
        });
      }
    } else {
      // Not a fault: this browser can't show notifications.
      logger.debug(
        "[ReduxReminder] Notifications not supported in this browser",
      );
    }
  }, []);

  // Check for due reminders every 5s
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const nextDue = reminderState.reminders.find(
        (r) =>
          !r.completed &&
          r.due !== undefined &&
          r.due <= now &&
          r.id !== lastNotifiedId.current,
      );

      if (nextDue) {
        logger.debug("[ReduxReminder] Reminder due:", nextDue);
        lastNotifiedId.current = nextDue.id;

        // Set active popup
        dispatch(setReminderPopup(nextDue.id));

        // Play sound
        if (ding) {
          ding.play().catch((err) => {
            logger.warn(
              "[ReduxReminder] Could not play notification sound:",
              err,
            );
          });
        }

        // Browser notification
        if ("Notification" in window && Notification.permission === "granted") {
          new Notification("Reminder", {
            body: nextDue.note?.title || "You have a note due!",
            icon: "/favicon.ico",
          });
        }
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [reminderState.reminders, dispatch, ding]);

  const addNewReminder = useCallback(
    (reminder: Reminder) => {
      dispatch(addReminder(reminder));
    },
    [dispatch],
  );

  const updateExistingReminder = useCallback(
    (reminder: Reminder) => {
      dispatch(updateReminder(reminder));
    },
    [dispatch],
  );

  const removeExistingReminder = useCallback(
    (id: number) => {
      dispatch(removeReminder(id));
    },
    [dispatch],
  );

  const dismissPopup = useCallback(() => {
    dispatch(setReminderPopup(null));
  }, [dispatch]);

  /**
   * A reminder for the note, due `minutes` from now; it replaces the note's
   * open one.
   */
  const createReminder = useCallback(
    (note: ReminderNote, minutes: number): Reminder => {
      const now = Date.now();
      const earlier = findNoteReminder(reminderState.reminders, note.id);
      if (earlier) dispatch(removeReminder(earlier.id));
      const reminder: Reminder = {
        id: now,
        noteId: note.id,
        note: { id: note.id, title: note.title, text: note.body ?? note.text },
        due: now + minutes * MINUTE,
        created: now,
      };
      addNewReminder(reminder);
      return reminder;
    },
    [addNewReminder, dispatch, reminderState.reminders],
  );

  // Service layer operations (consolidated from useReminderHooks)
  const getReminders = useCallback(async () => {
    const operation = withErrorHandling(
      async () => {
        return await reminderService.getReminders();
      },
      showBanner,
      "Loading reminders",
    );

    return await operation();
  }, [showBanner]);

  const getRemindersByNote = useCallback(
    async (noteId: number) => {
      const operation = withErrorHandling(
        async () => {
          return await reminderService.getRemindersByNote(noteId);
        },
        showBanner,
        "Loading note reminders",
      );

      return await operation();
    },
    [showBanner],
  );

  const createReminderWithService = useCallback(
    async (reminderData: CreateReminderData) => {
      const operation = withErrorHandling(
        async () => {
          const newReminder = await reminderService.createReminder(
            reminderData,
            userEmail,
          );
          await refreshAfterMutation();
          return newReminder;
        },
        showBanner,
        "Reminder creation",
        "Reminder created successfully",
      );

      return await operation();
    },
    [userEmail, refreshAfterMutation, showBanner],
  );

  const updateReminderWithService = useCallback(
    async (reminderId: number, reminderData: UpdateReminderData) => {
      const operation = withErrorHandling(
        async () => {
          const updatedReminder = await reminderService.updateReminder(
            reminderId,
            reminderData,
          );
          await refreshAfterMutation();
          return updatedReminder;
        },
        showBanner,
        "Reminder update",
        "Reminder updated successfully",
      );

      return await operation();
    },
    [refreshAfterMutation, showBanner],
  );

  const deleteReminderWithService = useCallback(
    async (reminderId: number, reminderTitle: string) => {
      logger.debug("Deleting reminder:", { reminderId, reminderTitle });

      const operation = withErrorHandling(
        async () => {
          await reminderService.deleteReminder(reminderId);
          showBanner(`Reminder "${reminderTitle}" deleted`);
          await refreshAfterMutation();
        },
        showBanner,
        "Reminder deletion",
      );

      await operation();
    },
    [refreshAfterMutation, showBanner],
  );

  /** Mark a reminder done, now. */
  const markCompleted = useCallback(
    (reminderId: number) => {
      const reminder = reminderState.reminders.find((r) => r.id === reminderId);
      if (reminder) {
        dispatch(updateReminder({ ...reminder, completed: Date.now() }));
      }
    },
    [dispatch, reminderState.reminders],
  );

  /** Move a reminder to `minutes` from now. */
  const snoozeReminder = useCallback(
    (reminderId: number, minutes: number) => {
      const reminder = reminderState.reminders.find((r) => r.id === reminderId);
      if (reminder) {
        dispatch(
          updateReminder({ ...reminder, due: Date.now() + minutes * MINUTE }),
        );
      }
    },
    [dispatch, reminderState.reminders],
  );

  const getUpcomingReminders = useCallback(
    async (withinHours = 24) => {
      const operation = withErrorHandling(
        async () => {
          return await reminderService.getUpcomingReminders(withinHours);
        },
        showBanner,
        "Loading upcoming reminders",
      );

      return await operation();
    },
    [showBanner],
  );

  // Enhanced reminder operations (from useReminderOperations)
  const setNoteReminder = useCallback(
    async (note: ReminderNote, minutes: number) => {
      try {
        const reminder = createReminder(note, minutes);
        return { success: true, reminder };
      } catch (error) {
        logger.error("Error setting reminder:", error);
        return { success: false, error: errorMessage(error) };
      }
    },
    [createReminder],
  );

  return {
    // State
    reminders: reminderState.reminders,
    activePopupId: reminderState.reminderPopup,
    showReminderDrawer: reminderState.showReminderDrawer,

    // Local state operations
    addReminder: addNewReminder,
    updateReminder: updateExistingReminder,
    removeReminder: removeExistingReminder,
    createReminder,
    dismissPopup,
    setActivePopupId: (id: ReminderPopup | null) =>
      dispatch(setReminderPopup(id)),

    // Service layer operations (from useReminderHooks)
    getReminders,
    getRemindersByNote,
    createReminderWithService,
    updateReminderWithService,
    deleteReminderWithService,
    markCompleted,
    snoozeReminder,
    getUpcomingReminders,

    // Enhanced operations (from useReminderOperations)
    setNoteReminder,
  };
}

// Convenience hook with same API as context
export const useReminderService = useReduxReminder;
